import React, { useEffect, useMemo, useState } from "react";
import Seo from "../components/SEO";

const MIN_HUMAN_DELAY_MS = 1800;

function nowMs() {
  return Date.now();
}

function normalizeUrl(raw) {
  if (!raw) return "";
  let v = raw.trim();
  if (!v) return "";
  if (!/^https?:\/\//i.test(v)) v = `https://${v}`;
  return v;
}

function safeJsonParse(raw, fallback) {
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

export default function ScopeBrief() {
  const FORMSPREE_URL = process.env.REACT_APP_FORMSPREE_SCOPE_URL || "";


  const [openedAt, setOpenedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState("");

  const storageKey = useMemo(() => "2mer_scope_brief_draft_v1", []);

  const [form, setForm] = useState({
    // Contact
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    businessName: "",
    businessAddress: "",
    currentWebsite: "",
    instagram: "",
    facebook: "",
    otherSocial: "",

    // Goals
    primaryGoal: "",
    secondaryGoals: "",
    idealCustomer: "",
    successLooksLike: "",

    // Pages
    pagesNeeded: "",
    mustHaveSections: "",
    contentNotes: "",

    // Branding
    hasLogo: "",
    logoWhere: "",
    brandColours: "",
    brandStyle: "",
    exampleSitesLike: "",
    exampleSitesAvoid: "",

    // Features
    features: {
      booking: false,
      contactForm: false,
      quoteForm: false,
      blog: false,
      gallery: false,
      reviews: false,
      ecommerce: false,
      memberships: false,
      multilingual: false,
      downloads: false,
      events: false,
      mapDirections: false,
    },
    featureNotes: "",

    // Integrations
    integrations: {
      ga4: false,
      gtm: false,
      googleBusinessProfile: false,
      metaPixel: false,
      mailchimpOrMailerLite: false,
      crm: false,
      payments: false,
      stripe: false,
      square: false,
    },
    integrationNotes: "",

    // SEO
    seoPriority: "",
    serviceAreas: "",
    keywords: "",
    competitors: "",

    // Compliance + access
    accessibilityNeeds: "",
    legalOrCompliance: "",
    domainOwned: "",
    domainRegistrar: "",
    hostingOwned: "",
    hostingProvider: "",
    accessNotes: "",

    // Timeline + budget
    timeline: "",
    launchDate: "",
    budgetRange: "",

    // Other
    anythingElse: "",

    // Honeypot
    companyWebsite: "",
  });

  useEffect(() => {
    setOpenedAt(nowMs());

    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const saved = safeJsonParse(raw, null);
      if (saved && typeof saved === "object") {
        setForm((prev) => ({
          ...prev,
          ...saved,
          features: { ...prev.features, ...(saved.features || {}) },
          integrations: { ...prev.integrations, ...(saved.integrations || {}) },
        }));
      }
    }
  }, [storageKey]);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(form));
  }, [form, storageKey]);

  function setField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function toggleNested(group, key) {
    setForm((prev) => ({
      ...prev,
      [group]: { ...prev[group], [key]: !prev[group][key] },
    }));
  }

  function clearDraft() {
    localStorage.removeItem(storageKey);
    setForm((prev) => ({
      ...prev,
      contactName: "",
      contactEmail: "",
      contactPhone: "",
      businessName: "",
      businessAddress: "",
      currentWebsite: "",
      instagram: "",
      facebook: "",
      otherSocial: "",
      primaryGoal: "",
      secondaryGoals: "",
      idealCustomer: "",
      successLooksLike: "",
      pagesNeeded: "",
      mustHaveSections: "",
      contentNotes: "",
      hasLogo: "",
      logoWhere: "",
      brandColours: "",
      brandStyle: "",
      exampleSitesLike: "",
      exampleSitesAvoid: "",
      features: Object.keys(prev.features).reduce((acc, k) => {
        acc[k] = false;
        return acc;
      }, {}),
      featureNotes: "",
      integrations: Object.keys(prev.integrations).reduce((acc, k) => {
        acc[k] = false;
        return acc;
      }, {}),
      integrationNotes: "",
      seoPriority: "",
      serviceAreas: "",
      keywords: "",
      competitors: "",
      accessibilityNeeds: "",
      legalOrCompliance: "",
      domainOwned: "",
      domainRegistrar: "",
      hostingOwned: "",
      hostingProvider: "",
      accessNotes: "",
      timeline: "",
      launchDate: "",
      budgetRange: "",
      anythingElse: "",
      companyWebsite: "",
    }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");

    if (form.companyWebsite && form.companyWebsite.trim()) {
      setErr("Submission blocked.");
      return;
    }

    if (!FORMSPREE_URL) {
      setErr("Missing form endpoint.");
      return;
    }

    const delay = openedAt ? Math.max(0, MIN_HUMAN_DELAY_MS - (nowMs() - openedAt)) : 0;

    setLoading(true);

    try {
      if (delay) await new Promise((r) => setTimeout(r, delay));

      const payload = {
        ...form,
        currentWebsite: normalizeUrl(form.currentWebsite),
        instagram: form.instagram.trim(),
        facebook: form.facebook.trim(),
        otherSocial: form.otherSocial.trim(),
        submittedAt: new Date().toISOString(),
        source: "2merative.com /scope-brief",
      };

      const res = await fetch(FORMSPREE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let msg = "Could not send.";
        try {
          const j = await res.json();
          if (j && j.error) msg = j.error;
        } catch {}
        throw new Error(msg);
      }

      setSubmitted(true);
      localStorage.removeItem(storageKey);
    } catch (error) {
      setErr(error?.message || "Could not send.");
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <>
        <Seo
          title="Project Brief Submitted"
          description="Thanks. Your project brief has been submitted."
          path="/scope-brief"
        />
        <main className="w-full px-4 sm:px-10 py-20 bg-white">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900">Submitted</h1>
            <p className="mt-4 text-slate-700">
              Thanks. Your project brief is in. I will review it and come back with next steps.
            </p>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Seo
        title="Website Project Brief"
        description="Scope collection form for website requirements."
        path="/scope-brief"
        noindex
      />

      <main className="w-full px-4 sm:px-10 py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto">
          <header className="mb-10">
            <h1 className="text-3xl sm:text-4xl font-semibold text-slate-900">
              Website Project Brief
            </h1>
            <p className="mt-3 text-slate-700">
              Fill in as much or as little as you like. Everything is optional.
            </p>
          </header>

          <form onSubmit={onSubmit} className="space-y-10">
            <input
              type="text"
              name="companyWebsite"
              value={form.companyWebsite}
              onChange={(e) => setField("companyWebsite", e.target.value)}
              autoComplete="off"
              tabIndex={-1}
              className="hidden"
            />

            <Section title="Contact basics">
              <Grid2>
                <Field label="Your name">
                  <Input value={form.contactName} onChange={(v) => setField("contactName", v)} />
                </Field>
                <Field label="Email">
                  <Input value={form.contactEmail} onChange={(v) => setField("contactEmail", v)} />
                </Field>
                <Field label="Phone">
                  <Input value={form.contactPhone} onChange={(v) => setField("contactPhone", v)} />
                </Field>
                <Field label="Business name">
                  <Input value={form.businessName} onChange={(v) => setField("businessName", v)} />
                </Field>
              </Grid2>

              <Field label="Business address">
                <Input value={form.businessAddress} onChange={(v) => setField("businessAddress", v)} />
              </Field>

              <Grid2>
                <Field label="Current website (if any)">
                  <Input
                    value={form.currentWebsite}
                    onChange={(v) => setField("currentWebsite", v)}
                    placeholder="example.com.au"
                  />
                </Field>
                <Field label="Instagram (optional)">
                  <Input value={form.instagram} onChange={(v) => setField("instagram", v)} placeholder="@handle or URL" />
                </Field>
                <Field label="Facebook (optional)">
                  <Input value={form.facebook} onChange={(v) => setField("facebook", v)} placeholder="Page URL" />
                </Field>
                <Field label="Other links (optional)">
                  <Input value={form.otherSocial} onChange={(v) => setField("otherSocial", v)} placeholder="TikTok, LinkedIn, YouTube" />
                </Field>
              </Grid2>
            </Section>

            <Section title="Goals and customers">
              <Field label="Primary goal">
                <Textarea value={form.primaryGoal} onChange={(v) => setField("primaryGoal", v)} placeholder="What should the website achieve?" />
              </Field>

              <Field label="Secondary goals">
                <Textarea value={form.secondaryGoals} onChange={(v) => setField("secondaryGoals", v)} placeholder="Anything else the site should help with?" />
              </Field>

              <Grid2>
                <Field label="Ideal customer">
                  <Textarea value={form.idealCustomer} onChange={(v) => setField("idealCustomer", v)} placeholder="Who is it for?" />
                </Field>
                <Field label="Success looks like">
                  <Textarea value={form.successLooksLike} onChange={(v) => setField("successLooksLike", v)} placeholder="How will we measure success?" />
                </Field>
              </Grid2>
            </Section>

            <Section title="Pages and content">
              <Field label="Pages you want">
                <Textarea
                  value={form.pagesNeeded}
                  onChange={(v) => setField("pagesNeeded", v)}
                  placeholder="Example: Home, About, Services, Pricing, FAQ, Contact, Booking, Locations"
                />
              </Field>

              <Field label="Must-have sections">
                <Textarea
                  value={form.mustHaveSections}
                  onChange={(v) => setField("mustHaveSections", v)}
                  placeholder="Example: testimonials, before/after, team, gallery, offers, lead magnet"
                />
              </Field>

              <Field label="Content readiness">
                <Textarea
                  value={form.contentNotes}
                  onChange={(v) => setField("contentNotes", v)}
                  placeholder="Do you have text and images ready? What needs writing or sourcing?"
                />
              </Field>
            </Section>

            <Section title="Brand and design">
              <Grid2>
                <Field label="Do you have a logo?">
                  <Select
                    value={form.hasLogo}
                    onChange={(v) => setField("hasLogo", v)}
                    options={[
                      { value: "", label: "Select (optional)" },
                      { value: "yes", label: "Yes" },
                      { value: "no", label: "No" },
                      { value: "not_sure", label: "Not sure" },
                    ]}
                  />
                </Field>
                <Field label="Where are your brand files?">
                  <Input value={form.logoWhere} onChange={(v) => setField("logoWhere", v)} placeholder="Google Drive link or notes" />
                </Field>
              </Grid2>

              <Grid2>
                <Field label="Colours you like">
                  <Textarea value={form.brandColours} onChange={(v) => setField("brandColours", v)} placeholder="Hex codes or examples" />
                </Field>
                <Field label="Style notes">
                  <Textarea value={form.brandStyle} onChange={(v) => setField("brandStyle", v)} placeholder="Modern, warm, clinical, luxe, playful, bold" />
                </Field>
              </Grid2>

              <Grid2>
                <Field label="Websites you like">
                  <Textarea value={form.exampleSitesLike} onChange={(v) => setField("exampleSitesLike", v)} placeholder="Paste URLs" />
                </Field>
                <Field label="Websites to avoid">
                  <Textarea value={form.exampleSitesAvoid} onChange={(v) => setField("exampleSitesAvoid", v)} placeholder="Paste URLs" />
                </Field>
              </Grid2>
            </Section>

            <Section title="Features">
              <div className="grid sm:grid-cols-2 gap-3">
                <Check label="Booking" checked={form.features.booking} onChange={() => toggleNested("features", "booking")} />
                <Check label="Contact form" checked={form.features.contactForm} onChange={() => toggleNested("features", "contactForm")} />
                <Check label="Quote / scope form" checked={form.features.quoteForm} onChange={() => toggleNested("features", "quoteForm")} />
                <Check label="Blog" checked={form.features.blog} onChange={() => toggleNested("features", "blog")} />
                <Check label="Gallery / portfolio" checked={form.features.gallery} onChange={() => toggleNested("features", "gallery")} />
                <Check label="Reviews" checked={form.features.reviews} onChange={() => toggleNested("features", "reviews")} />
                <Check label="Ecommerce" checked={form.features.ecommerce} onChange={() => toggleNested("features", "ecommerce")} />
                <Check label="Membership / login" checked={form.features.memberships} onChange={() => toggleNested("features", "memberships")} />
                <Check label="Multi-language" checked={form.features.multilingual} onChange={() => toggleNested("features", "multilingual")} />
                <Check label="Downloads / PDFs" checked={form.features.downloads} onChange={() => toggleNested("features", "downloads")} />
                <Check label="Events" checked={form.features.events} onChange={() => toggleNested("features", "events")} />
                <Check label="Maps / directions" checked={form.features.mapDirections} onChange={() => toggleNested("features", "mapDirections")} />
              </div>

              <Field label="Feature notes">
                <Textarea value={form.featureNotes} onChange={(v) => setField("featureNotes", v)} placeholder="Any details about how features should work" />
              </Field>
            </Section>

            <Section title="Integrations">
              <div className="grid sm:grid-cols-2 gap-3">
                <Check label="GA4" checked={form.integrations.ga4} onChange={() => toggleNested("integrations", "ga4")} />
                <Check label="Google Tag Manager" checked={form.integrations.gtm} onChange={() => toggleNested("integrations", "gtm")} />
                <Check label="Google Business Profile" checked={form.integrations.googleBusinessProfile} onChange={() => toggleNested("integrations", "googleBusinessProfile")} />
                <Check label="Meta Pixel" checked={form.integrations.metaPixel} onChange={() => toggleNested("integrations", "metaPixel")} />
                <Check label="Email marketing" checked={form.integrations.mailchimpOrMailerLite} onChange={() => toggleNested("integrations", "mailchimpOrMailerLite")} />
                <Check label="CRM" checked={form.integrations.crm} onChange={() => toggleNested("integrations", "crm")} />
                <Check label="Payments" checked={form.integrations.payments} onChange={() => toggleNested("integrations", "payments")} />
                <Check label="Stripe" checked={form.integrations.stripe} onChange={() => toggleNested("integrations", "stripe")} />
                <Check label="Square" checked={form.integrations.square} onChange={() => toggleNested("integrations", "square")} />
              </div>

              <Field label="Integration notes">
                <Textarea value={form.integrationNotes} onChange={(v) => setField("integrationNotes", v)} placeholder="Accounts you already use, or what you want to connect" />
              </Field>
            </Section>

            <Section title="SEO and visibility">
              <Field label="SEO priority">
                <Textarea value={form.seoPriority} onChange={(v) => setField("seoPriority", v)} placeholder="Local SEO, service pages, blog strategy, maps, conversions" />
              </Field>
              <Grid2>
                <Field label="Service areas">
                  <Textarea value={form.serviceAreas} onChange={(v) => setField("serviceAreas", v)} placeholder="Suburbs, regions, city" />
                </Field>
                <Field label="Keywords or topics">
                  <Textarea value={form.keywords} onChange={(v) => setField("keywords", v)} placeholder="What do people search?" />
                </Field>
              </Grid2>
              <Field label="Competitors">
                <Textarea value={form.competitors} onChange={(v) => setField("competitors", v)} placeholder="Paste competitor URLs" />
              </Field>
            </Section>

            <Section title="Access, hosting, and compliance">
              <Grid2>
                <Field label="Do you already own the domain?">
                  <Select
                    value={form.domainOwned}
                    onChange={(v) => setField("domainOwned", v)}
                    options={[
                      { value: "", label: "Select (optional)" },
                      { value: "yes", label: "Yes" },
                      { value: "no", label: "No" },
                      { value: "not_sure", label: "Not sure" },
                    ]}
                  />
                </Field>
                <Field label="Domain registrar (if known)">
                  <Input value={form.domainRegistrar} onChange={(v) => setField("domainRegistrar", v)} placeholder="CrazyDomains, GoDaddy, VentraIP, etc." />
                </Field>
              </Grid2>

              <Grid2>
                <Field label="Do you have hosting already?">
                  <Select
                    value={form.hostingOwned}
                    onChange={(v) => setField("hostingOwned", v)}
                    options={[
                      { value: "", label: "Select (optional)" },
                      { value: "yes", label: "Yes" },
                      { value: "no", label: "No" },
                      { value: "not_sure", label: "Not sure" },
                    ]}
                  />
                </Field>
                <Field label="Hosting provider (if known)">
                  <Input value={form.hostingProvider} onChange={(v) => setField("hostingProvider", v)} placeholder="Hostinger, GoDaddy, etc." />
                </Field>
              </Grid2>

              <Grid2>
                <Field label="Accessibility needs">
                  <Textarea value={form.accessibilityNeeds} onChange={(v) => setField("accessibilityNeeds", v)} placeholder="Any requirements or target level" />
                </Field>
                <Field label="Legal or compliance notes">
                  <Textarea value={form.legalOrCompliance} onChange={(v) => setField("legalOrCompliance", v)} placeholder="Industry rules, disclaimers, approvals" />
                </Field>
              </Grid2>

              <Field label="Access notes">
                <Textarea value={form.accessNotes} onChange={(v) => setField("accessNotes", v)} placeholder="Who will provide logins, assets, copy, approvals" />
              </Field>
            </Section>

            <Section title="Timeline and budget">
              <Grid2>
                <Field label="Timeline">
                  <Input value={form.timeline} onChange={(v) => setField("timeline", v)} placeholder="Example: ASAP, 4 weeks, 8 weeks" />
                </Field>
                <Field label="Ideal launch date">
                  <Input value={form.launchDate} onChange={(v) => setField("launchDate", v)} placeholder="Optional" />
                </Field>
              </Grid2>

              <Field label="Budget range (optional)">
                <Input value={form.budgetRange} onChange={(v) => setField("budgetRange", v)} placeholder="Example: 3–5k, 5–8k, not sure" />
              </Field>
            </Section>

            <Section title="Anything else">
              <Field label="Notes">
                <Textarea value={form.anythingElse} onChange={(v) => setField("anythingElse", v)} placeholder="Anything you want me to know" />
              </Field>
            </Section>

            {err ? (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-800">
                {err}
              </div>
            ) : null}

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between pt-2">
              <button
                type="button"
                onClick={clearDraft}
                className="px-5 py-3 rounded-full border border-slate-300 text-slate-800 hover:bg-slate-50"
              >
                Clear draft
              </button>

              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 rounded-full bg-slate-900 text-white disabled:opacity-60"
              >
                {loading ? "Sending..." : "Submit brief"}
              </button>
            </div>

            <p className="text-sm text-slate-500">
              This form saves a draft on this device until submitted.
            </p>
          </form>
        </div>
      </main>
    </>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 p-5 sm:p-7">
      <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
      <div className="mt-4 space-y-4">{children}</div>
    </section>
  );
}

function Grid2({ children }) {
  return <div className="grid md:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-sm font-medium text-slate-800 mb-2">{label}</div>
      {children}
    </label>
  );
}

function Input({ value, onChange, placeholder = "" }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:ring-2 focus:ring-slate-300"
    />
  );
}

function Textarea({ value, onChange, placeholder = "" }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={4}
      className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:ring-2 focus:ring-slate-300"
    />
  );
}

function Select({ value, onChange, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-xl border border-slate-300 px-4 py-3 bg-white outline-none focus:ring-2 focus:ring-slate-300"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function Check({ label, checked, onChange }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 px-4 py-3 hover:bg-slate-50"
      aria-pressed={checked}
    >
      <span className="text-slate-800">{label}</span>
      <span
        className={`h-5 w-5 rounded border ${
          checked ? "bg-slate-900 border-slate-900" : "bg-white border-slate-300"
        }`}
      />
    </button>
  );
}
